import React, { useState, useEffect } from 'react';
import { getLabels } from '../fieldLabels';

const API_BASE = "https://adria.terrier-ule.ts.net";

// 템플릿별 표시 컬럼 설정
const COLUMNS = {
  residential: [
    { key: 'room_no',      label: null,        suffix: 'room_suffix' },
    { key: 'user_name',    label: null },
    { key: 'phone',        label: null },
    { key: 'monthly_rent', label: '월세',       format: 'money' },
    { key: 'deposit',      label: '보증금',     format: 'money' },
    { key: 'expiry_date',  label: '만기일' },
  ],
  parking: [
    { key: 'room_no',      label: null },
    { key: 'user_name',    label: null },
    { key: 'phone',        label: null },
    { key: 'move_in_date', label: '등록일' },
    { key: 'expiry_date',  label: '만료일' },
  ],
  storage: [
    { key: 'room_no',      label: null },
    { key: 'user_name',    label: null },
    { key: 'phone',        label: null },
    { key: 'monthly_rent', label: '이용료',     format: 'money' },
    { key: 'expiry_date',  label: '계약만료' },
  ],
  store: [
    { key: 'room_no',      label: null },
    { key: 'user_name',    label: null },
    { key: 'phone',        label: null },
    { key: 'expiry_date',  label: '만료일' },
  ],
};

// 템플릿별 폼 필드
const FORM_FIELDS = {
  residential: ['room_no', 'user_name', 'phone', 'car_number', 'identification', 'email', 'deposit', 'monthly_rent', 'mgmt_fee', 'move_in_date', 'expiry_date', 'memo'],
  parking:     ['room_no', 'user_name', 'phone', 'car_number', 'move_in_date', 'expiry_date', 'memo'],
  storage:     ['room_no', 'user_name', 'phone', 'monthly_rent', 'move_in_date', 'expiry_date', 'memo'],
  store:       ['room_no', 'user_name', 'phone', 'move_in_date', 'expiry_date', 'memo'],
};

export default function RosterPage({ token, tenantId, template, onBack }) {
  const L       = getLabels(template);
  const cols    = COLUMNS[template] || COLUMNS.residential;
  const fields  = FORM_FIELDS[template] || FORM_FIELDS.residential;

  const [rooms,         setRooms]         = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode,    setMode]    = useState('list'); // 'list' | 'edit' | 'add'
  const [selRoom, setSelRoom] = useState(null);
  const [form,    setForm]    = useState({});
  const [msg,     setMsg]     = useState('');
  const [saving,  setSaving]  = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => { fetchRooms(); }, []);

  const fetchRooms = async (withInactive = false) => {
    setLoading(true);
    try {
      const url = withInactive ? `${API_BASE}/api/rooms?include_inactive=true` : `${API_BASE}/api/rooms`;
      const res  = await fetch(url, { headers });
      const data = await res.json();
      setRooms(Array.isArray(data) ? data : []);
    } catch { setMsg('❌ 목록 로드 실패'); }
    finally { setLoading(false); }
  };

  const openEdit = async (room) => {
    setSelRoom(room);
    setMode('edit');
    setMsg('');
    // 개별 조회로 주민번호 복호화 값 가져오기
    try {
      const res  = await fetch(`${API_BASE}/api/rooms/${room.id}`, { headers });
      const data = await res.json();
      setForm({
        room_no:        data.room_no      || '',
        user_name:      data.user_name    || '',
        phone:          data.phone        || '',
        car_number:     data.car_number   || '',
        email:          data.email        || '',
        deposit:        data.deposit      || '',
        monthly_rent:   data.monthly_rent || '',
        mgmt_fee:       data.mgmt_fee     || '',
        move_in_date:   data.move_in_date || '',
        expiry_date:    data.expiry_date  || '',
        memo:           data.memo         || '',
        identification: data.identification || '',
        occupants:      (data.occupants || []).map(o => ({ user_name: o.user_name, phone: o.phone })),
      });
    } catch {
      setForm({
        room_no:        room.room_no      || '',
        user_name:      room.user_name    || '',
        phone:          room.phone        || '',
        car_number:     room.car_number   || '',
        email:          room.email        || '',
        deposit:        room.deposit      || '',
        monthly_rent:   room.monthly_rent || '',
        mgmt_fee:       room.mgmt_fee     || '',
        move_in_date:   room.move_in_date || '',
        expiry_date:    room.expiry_date  || '',
        memo:           room.memo         || '',
        identification: '',
        occupants:      (room.occupants || []).map(o => ({ user_name: o.user_name, phone: o.phone })),
      });
    }
  };

  const openAdd = () => {
    setSelRoom(null);
    setForm({});
    setMode('add');
    setMsg('');
  };

  const save = async () => {
    if (!form.room_no || !form.user_name || !form.phone) {
      setMsg('❌ ' + L.required_msg); return;
    }
    setSaving(true); setMsg('');
    try {
      const url    = mode === 'add' ? `${API_BASE}/api/rooms` : `${API_BASE}/api/rooms/${selRoom.id}`;
      const method = mode === 'add' ? 'POST' : 'PATCH';
      // PATCH 시 room_no는 수정 불가 필드라 제외
      const { room_no, ...patchForm } = form;

      // PATCH: 원래 값과 비교해서 실제로 변경된 필드만 전송
      const INT_FIELDS  = ['deposit', 'monthly_rent', 'mgmt_fee'];
      const DATE_FIELDS = ['move_in_date', 'expiry_date'];
      const toIntOrNull  = (v) => (v === '' || v == null) ? null : parseInt(v, 10) || 0;
      const toDateOrNull = (v) => (v === '' || v == null) ? null : v;

      const normalizeVal = (key, v) => {
        if (INT_FIELDS.includes(key))  return toIntOrNull(v);
        if (DATE_FIELDS.includes(key)) return toDateOrNull(v);
        return v === '' ? null : v;
      };

      let body;
      if (mode === 'add') {
        body = form;
      } else {
        // 변경된 필드만 추출 (occupants는 배열이라 아래에서 별도 비교)
        body = {};
        Object.keys(patchForm).forEach(key => {
          if (key === 'occupants') return;
          const newVal  = normalizeVal(key, patchForm[key]);
          const origVal = normalizeVal(key, selRoom[key] ?? '');
          if (String(newVal) !== String(origVal)) {
            body[key] = newVal;
          }
        });
        const newOccupants  = form.occupants || [];
        const origOccupants = (selRoom.occupants || []).map(o => ({ user_name: o.user_name, phone: o.phone }));
        if (JSON.stringify(newOccupants) !== JSON.stringify(origOccupants)) {
          body.occupants = newOccupants;
        }
        if (Object.keys(body).length === 0) {
          setMsg('변경된 내용이 없습니다.'); setSaving(false); return;
        }
      }

      const res    = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (res.ok) {
        setMsg('✅ 저장 완료');
        fetchRooms();
        setTimeout(() => setMode('list'), 800);
      } else {
        const d = await res.json();
        setMsg('❌ ' + (d.detail || '저장 실패'));
      }
    } catch { setMsg('❌ 서버 오류'); }
    finally { setSaving(false); }
  };

  const deactivate = async (room) => {
    if (!window.confirm(`${room.room_no}${L.room_suffix} ${room.user_name}님을 ${L.delete_confirm} 처리할까요?`)) return;
    try {
      await fetch(`${API_BASE}/api/rooms/${room.id}`, { method: 'DELETE', headers });
      fetchRooms();
    } catch { setMsg('❌ 처리 실패'); }
  };

  const activate = async (room) => {
    if (!window.confirm(`${room.room_no}${L.room_suffix} ${room.user_name}님을 다시 활성화할까요?`)) return;
    try {
      await fetch(`${API_BASE}/api/rooms/${room.id}`, { method: 'PATCH', headers, body: JSON.stringify({ is_active: true }) });
      fetchRooms(includeInactive);
    } catch { setMsg('❌ 처리 실패'); }
  };

  const hardDelete = async (room) => {
    if (!window.confirm(`⚠️ ${room.room_no}${L.room_suffix} ${room.user_name}님 데이터를 완전히 삭제합니다.\n복구가 불가능합니다. 계속할까요?`)) return;
    try {
      await fetch(`${API_BASE}/api/rooms/${room.id}/hard`, { method: 'DELETE', headers });
      fetchRooms(includeInactive);
    } catch { setMsg('❌ 삭제 실패'); }
  };

  const fmtMoney = (v) => v ? `${Number(v).toLocaleString()}원` : '-';

  // ── 목록 화면 ─────────────────────────────────────────────
  if (mode === 'list') return (
    <div style={{ padding: '16px', fontFamily: '"Pretendard", sans-serif', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>

        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {onBack && (
              <button onClick={onBack}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>
                ←
              </button>
            )}
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#111827' }}>
              {L.room_no} 관리
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={openAdd}
              style={{ padding: '8px 16px', backgroundColor: '#4f46e5', color: '#fff',
                       border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
              {L.add_btn}
            </button>
            <button onClick={() => { const next = !includeInactive; setIncludeInactive(next); fetchRooms(next); }}
              style={{ padding: '8px 12px', backgroundColor: includeInactive ? '#fef3c7' : '#f1f5f9',
                       color: includeInactive ? '#b45309' : '#6b7280', border: 'none', borderRadius: '10px',
                       fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>
              {includeInactive ? '🔴 퇴거 포함' : '⚪ 퇴거 포함'}
            </button>
          </div>
        </div>

        {msg && (
          <div style={{ padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px',
                        backgroundColor: msg.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
                        color: msg.startsWith('✅') ? '#15803d' : '#dc2626', fontWeight: 'bold' }}>
            {msg}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>로딩 중...</div>
        ) : rooms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>등록된 {L.user_name}이 없습니다</div>
        ) : rooms.map(room => (
          <div key={room.id} style={{ backgroundColor: room.is_active ? '#fff' : '#f9fafb', borderRadius: '14px', padding: '14px 16px',
                                       marginBottom: '10px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', opacity: room.is_active ? 1 : 0.7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>
                  {room.room_no}{L.room_suffix} &nbsp; {room.user_name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>{room.phone}</div>
                {room.car_number && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{room.car_number}</div>
                )}
                {template === 'residential' && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    월세 {fmtMoney(room.monthly_rent)} · 만기 {room.expiry_date || '-'}
                  </div>
                )}
                {template === 'parking' && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    등록일 {room.move_in_date || '-'} · 만료 {room.expiry_date || '-'}
                  </div>
                )}
                {template === 'storage' && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    이용료 {fmtMoney(room.monthly_rent)} · 만료 {room.expiry_date || '-'}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => openEdit(room)}
                  style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#f3f4f6',
                           color: '#4f46e5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  수정
                </button>
                {room.is_active ? (
                  <button onClick={() => deactivate(room)}
                    style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#fee2e2',
                             color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    {L.delete_confirm}
                  </button>
                ) : (
                  <button onClick={() => activate(room)}
                    style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#d1fae5',
                             color: '#065f46', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    ✅ 활성
                  </button>
                )}
                <button onClick={() => hardDelete(room)}
                  style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#f3f4f6',
                           color: '#9ca3af', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  🗑 삭제
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── 등록/수정 폼 ──────────────────────────────────────────
  return (
    <div style={{ padding: '16px', fontFamily: '"Pretendard", sans-serif', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>

        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <button onClick={() => setMode('list')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>
            ←
          </button>
          <div style={{ fontSize: '18px', fontWeight: '800', color: '#111827' }}>
            {mode === 'add' ? L.add_btn : `${selRoom?.room_no}${L.room_suffix} 수정`}
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>

          {msg && (
            <div style={{ padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px',
                          backgroundColor: msg.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
                          color: msg.startsWith('✅') ? '#15803d' : '#dc2626', fontWeight: 'bold' }}>
              {msg}
            </div>
          )}

          {/* room_no */}
          {fields.includes('room_no') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>{L.room_no} *</label>
              <input value={form.room_no || ''} readOnly={mode === 'edit'}
                onChange={e => setForm(f => ({ ...f, room_no: e.target.value }))}
                placeholder={L.room_no_ph}
                style={{ ...inp, backgroundColor: mode === 'edit' ? '#f9fafb' : '#fff',
                         color: mode === 'edit' ? '#9ca3af' : '#111' }} />
            </div>
          )}

          {/* user_name */}
          {fields.includes('user_name') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>{L.user_name} *</label>
              <input value={form.user_name || ''}
                onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))}
                placeholder={L.user_name_ph} style={inp} />
            </div>
          )}

          {/* identification */}
          {fields.includes('identification') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>주민등록번호</label>
              <input value={form.identification || ''} type="text"
                onChange={e => setForm(f => ({ ...f, identification: e.target.value }))}
                placeholder="주민등록번호 (저장 시 암호화)" style={inp} />
            </div>
          )}
          {/* phone */}
          {fields.includes('phone') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>{L.phone} *</label>
              <input value={form.phone || ''} type="tel"
                onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder={L.phone_ph} style={inp} />
            </div>
          )}

          {/* car_number */}
          {fields.includes('car_number') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>차량번호</label>
              <input value={form.car_number || ''} type="text"
                onChange={e => setForm(f => ({ ...f, car_number: e.target.value }))}
                placeholder="예: 12가3456" style={inp} />
            </div>
          )}

          {/* email — residential만 */}
          {fields.includes('email') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>이메일</label>
              <input value={form.email || ''}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="example@email.com" style={inp} />
            </div>
          )}

          {/* 금액 — residential/storage */}
          {fields.includes('deposit') && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>보증금</label>
                <input type="number" value={form.deposit ?? ''}
                  onChange={e => setForm(f => ({ ...f, deposit: e.target.value }))}
                  placeholder="0" style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>월세</label>
                <input type="number" value={form.monthly_rent ?? ''}
                  onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))}
                  placeholder="0" style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>관리비</label>
                <input type="number" value={form.mgmt_fee ?? ''}
                  onChange={e => setForm(f => ({ ...f, mgmt_fee: e.target.value }))}
                  placeholder="0" style={inp} />
              </div>
            </div>
          )}

          {/* 이용료 — storage */}
          {fields.includes('monthly_rent') && !fields.includes('deposit') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>이용료</label>
              <input type="number" value={form.monthly_rent ?? ''}
                onChange={e => setForm(f => ({ ...f, monthly_rent: e.target.value }))}
                placeholder="0" style={inp} />
            </div>
          )}

          {/* 날짜 */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {fields.includes('move_in_date') && (
              <div style={{ flex: 1 }}>
                <label style={lbl}>{template === 'residential' ? '입주일' : '등록일'}</label>
                <input type="date" value={form.move_in_date || ''}
                  onChange={e => setForm(f => ({ ...f, move_in_date: e.target.value }))} style={inp} />
              </div>
            )}
            {fields.includes('expiry_date') && (
              <div style={{ flex: 1 }}>
                <label style={lbl}>{L.expires_at}</label>
                <input type="date" value={form.expiry_date || ''}
                  onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} style={inp} />
              </div>
            )}
          </div>

          {/* 동거인 — residential(email 필드 있는 템플릿)에서만 표시 */}
          {fields.includes('email') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={lbl}>동거인 (선택)</label>
              {(form.occupants || []).map((occ, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <input
                    value={occ.user_name || ''}
                    onChange={e => setForm(f => {
                      const occupants = [...(f.occupants || [])];
                      occupants[idx] = { ...occupants[idx], user_name: e.target.value };
                      return { ...f, occupants };
                    })}
                    placeholder="이름"
                    style={{ ...inp, flex: 1, marginBottom: 0 }}
                  />
                  <input
                    value={occ.phone || ''}
                    onChange={e => setForm(f => {
                      const occupants = [...(f.occupants || [])];
                      occupants[idx] = { ...occupants[idx], phone: e.target.value.replace(/[^0-9]/g, '') };
                      return { ...f, occupants };
                    })}
                    placeholder="01012345678"
                    inputMode="tel"
                    style={{ ...inp, flex: 1, marginBottom: 0 }}
                  />
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, occupants: (f.occupants || []).filter((_, i) => i !== idx) }))}
                    style={{ padding: '0 12px', border: 'none', borderRadius: '8px', background: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}>
                    삭제
                  </button>
                </div>
              ))}
              <button type="button"
                onClick={() => setForm(f => ({ ...f, occupants: [...(f.occupants || []), { user_name: '', phone: '' }] }))}
                style={{ padding: '6px 12px', border: '1px dashed #9ca3af', borderRadius: '8px', background: 'transparent', cursor: 'pointer' }}>
                + 동거인 추가
              </button>
            </div>
          )}

          {/* 메모 */}
          {fields.includes('memo') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={lbl}>메모</label>
              <textarea value={form.memo || ''} rows={2}
                onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
                placeholder="메모"
                style={{ ...inp, resize: 'none', marginBottom: 0 }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setMode('list')}
              style={{ flex: 1, padding: '12px', backgroundColor: '#e5e7eb', color: '#4b5563',
                       border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}>
              취소
            </button>
            <button onClick={save} disabled={saving}
              style={{ flex: 2, padding: '12px', backgroundColor: saving ? '#9ca3af' : '#4f46e5',
                       color: '#fff', border: 'none', borderRadius: '10px',
                       fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}>
              {saving ? '저장 중...' : '✅ 저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px', fontWeight: '600' };
const inp = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db',
              fontSize: '14px', boxSizing: 'border-box', outline: 'none', marginBottom: 0 };
